import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, MessageCircle, Star, ThumbsUp } from "lucide-react";
import { useEffect, useState } from "react";

interface Review {
	id: string;
	name: string;
	rating: number;
	comment: string | null;
	date: string;
	helpfulCount: number;
	isCurrentUserReview?: boolean;
}

interface ProductReviewsProps {
	productId: string;
}

export function ProductReviews({ productId }: ProductReviewsProps) {
	const [reviews, setReviews] = useState<Review[]>([]);
	const [loading, setLoading] = useState(true);
	const [newRating, setNewRating] = useState(5);
	const [newComment, setNewComment] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [userHasReviewed, setUserHasReviewed] = useState(false);
	const [showReviewForm, setShowReviewForm] = useState(false);
	const { toast } = useToast();
	const { user } = useAuth();

	useEffect(() => {
		if (productId) {
			fetchReviews();
		}
	}, [productId]);

	const fetchReviews = async () => {
		try {
			setLoading(true);

			// Fetch reviews for this product
			const { data: reviewsData, error: reviewsError } = await supabase
				.from("product_reviews")
				.select(
					`
          id,
          rating,
          comment,
          created_at,
          user_id
        `
				)
				.eq("product_id", productId)
				.order("created_at", { ascending: false });

			if (reviewsError) throw reviewsError;

			// If no reviews, set empty array and return
			if (!reviewsData || reviewsData.length === 0) {
				setReviews([]);
				setLoading(false);
				return;
			}

			// Get unique user IDs from reviews
			const userIds = [
				...new Set(reviewsData.map((review) => review.user_id)),
			];

			// Fetch user profiles
			const { data: profiles, error: profilesError } = await supabase
				.from("profiles")
				.select("id, name, business_name")
				.in("id", userIds);

			if (profilesError) throw profilesError;

			// Check if current user has already reviewed
			if (user) {
				const hasReviewed = reviewsData.some(
					(review) => review.user_id === user.id
				);
				setUserHasReviewed(hasReviewed);
			}

			// Map reviews with user names
			const formattedReviews = reviewsData.map((review) => {
				const userProfile = profiles?.find(
					(profile) => profile.id === review.user_id
				);
				const displayName = userProfile
					? userProfile.business_name ||
					  userProfile.name ||
					  "Anonymous User"
					: "Anonymous User";
				const reviewDate = new Date(review.created_at);

				return {
					id: review.id,
					name: displayName,
					rating: review.rating,
					comment: review.comment,
					date: reviewDate.toLocaleDateString(),
					helpfulCount: 0, // Not implemented in database yet
					isCurrentUserReview: user
						? review.user_id === user.id
						: false,
				};
			});

			setReviews(formattedReviews);
		} catch (error) {
			console.error("Error fetching reviews:", error);
			toast({
				title: "Error",
				description: "Failed to load reviews. Please try again later.",
				variant: "destructive",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleSubmitReview = async () => {
		if (!user) {
			toast({
				title: "Authentication required",
				description: "Please log in to submit a review",
				variant: "destructive",
			});
			return;
		}

		try {
			setSubmitting(true);

			const { error } = await supabase.from("product_reviews").insert({
				product_id: productId,
				user_id: user.id,
				rating: newRating,
				comment: newComment.trim() || null,
			});

			if (error) throw error;

			toast({
				title: "Review submitted",
				description: "Thank you for your feedback!",
			});

			// Reset form and refresh reviews
			setNewRating(5);
			setNewComment("");
			setShowReviewForm(false);
			setUserHasReviewed(true);
			fetchReviews();
		} catch (error: any) {
			console.error("Error submitting review:", error);

			// Handle unique constraint violation (user already reviewed)
			if (error.code === "23505") {
				toast({
					title: "Already reviewed",
					description:
						"You have already submitted a review for this product",
					variant: "destructive",
				});
				setUserHasReviewed(true);
			} else {
				toast({
					title: "Error",
					description:
						"Failed to submit review. Please try again later.",
					variant: "destructive",
				});
			}
		} finally {
			setSubmitting(false);
		}
	};

	const handleDeleteReview = async (reviewId: string) => {
		if (!user) return;

		try {
			const { error } = await supabase
				.from("product_reviews")
				.delete()
				.eq("id", reviewId)
				.eq("user_id", user.id);

			if (error) throw error;

			toast({
				title: "Review deleted",
				description: "Your review has been removed",
			});

			setUserHasReviewed(false);
			fetchReviews();
		} catch (error) {
			console.error("Error deleting review:", error);
			toast({
				title: "Error",
				description: "Failed to delete review. Please try again later.",
				variant: "destructive",
			});
		}
	};

	const renderStars = (rating: number) => {
		return (
			<div className="flex">
				{Array.from({ length: 5 }).map((_, i) => (
					<Star
						key={i}
						className={`h-4 w-4 ${
							i < rating
								? "text-yellow-400 fill-yellow-400"
								: "text-gray-300"
						}`}
					/>
				))}
			</div>
		);
	};

	const renderStarInput = () => {
		return (
			<div className="flex items-center gap-2 mb-4">
				<div className="text-sm text-gray-600">Rating:</div>
				<div className="flex">
					{Array.from({ length: 5 }).map((_, i) => (
						<button
							key={i}
							type="button"
							onClick={() => setNewRating(i + 1)}
							className="focus:outline-none">
							<Star
								className={`h-6 w-6 cursor-pointer ${
									i < newRating
										? "text-yellow-400 fill-yellow-400"
										: "text-gray-300"
								}`}
							/>
						</button>
					))}
				</div>
			</div>
		);
	};

	if (loading) {
		return (
			<div className="space-y-4">
				{Array.from({ length: 3 }).map((_, i) => (
					<Card key={i}>
						<CardContent className="p-6">
							<div className="flex items-center gap-2 mb-2">
								<Skeleton className="h-4 w-24" />
								<Skeleton className="h-4 w-16" />
							</div>
							<Skeleton className="h-4 w-32 mb-4" />
							<Skeleton className="h-16 w-full mb-4" />
							<Skeleton className="h-8 w-28" />
						</CardContent>
					</Card>
				))}
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Reviews summary */}
			<div className="bg-gray-50 rounded-lg p-6 mb-8">
				<div className="flex items-center gap-4">
					<div className="text-4xl font-display font-bold text-gray-900">
						{reviews.length > 0
							? (
									reviews.reduce(
										(acc, review) => acc + review.rating,
										0
									) / reviews.length
							  ).toFixed(1)
							: "0"}
					</div>
					<div>
						<div className="flex mb-1">
							{renderStars(
								reviews.length > 0
									? Math.round(
											reviews.reduce(
												(acc, review) =>
													acc + review.rating,
												0
											) / reviews.length
									  )
									: 0
							)}
						</div>
						<div className="text-sm text-gray-500">
							Based on {reviews.length} review
							{reviews.length !== 1 ? "s" : ""}
						</div>
					</div>
				</div>
			</div>

			{/* Review form */}
			{user && !userHasReviewed && (
				<div>
					{showReviewForm ? (
						<Card>
							<CardContent className="p-6">
								<h3 className="font-medium text-gray-900 mb-4">
									Write Your Review
								</h3>
								{renderStarInput()}
								<Textarea
									placeholder="Share your experience with this product..."
									value={newComment}
									onChange={(e) =>
										setNewComment(e.target.value)
									}
									className="mb-4"
								/>
								<div className="flex gap-2">
									<Button
										onClick={handleSubmitReview}
										disabled={submitting}>
										{submitting ? (
											<>
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												Submitting...
											</>
										) : (
											"Submit Review"
										)}
									</Button>
									<Button
										variant="outline"
										onClick={() => setShowReviewForm(false)}
										disabled={submitting}>
										Cancel
									</Button>
								</div>
							</CardContent>
						</Card>
					) : (
						<div className="text-center mb-6">
							<Button onClick={() => setShowReviewForm(true)}>
								Write a Review
							</Button>
						</div>
					)}
				</div>
			)}

			{/* Individual reviews */}
			{reviews.length > 0 ? (
				<div className="space-y-6">
					{reviews.map((review) => (
						<Card key={review.id}>
							<CardContent className="p-6">
								<div className="flex justify-between items-start mb-4">
									<div>
										<h4 className="font-medium text-gray-900 mb-1">
											{review.name}
										</h4>
										{renderStars(review.rating)}
									</div>
									<span className="text-sm text-gray-500">
										{review.date}
									</span>
								</div>

								{review.comment && (
									<p className="text-gray-700 mb-4">
										{review.comment}
									</p>
								)}

								<div className="flex justify-between">
									<Button
										variant="ghost"
										size="sm"
										className="text-gray-500">
										<ThumbsUp className="h-4 w-4 mr-2" />
										Helpful ({review.helpfulCount})
									</Button>

									{review.isCurrentUserReview && (
										<Button
											variant="ghost"
											size="sm"
											className="text-red-500"
											onClick={() =>
												handleDeleteReview(review.id)
											}>
											Delete Review
										</Button>
									)}
								</div>
							</CardContent>
						</Card>
					))}
				</div>
			) : (
				<Card>
					<CardContent className="p-8 text-center">
						<MessageCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
						<h3 className="text-xl font-medium text-gray-900 mb-2">
							No reviews yet
						</h3>
						<p className="text-gray-500 mb-6">
							Be the first to review this product
						</p>
						{user && !userHasReviewed ? (
							<Button onClick={() => setShowReviewForm(true)}>
								Write a Review
							</Button>
						) : !user ? (
							<p className="text-gray-500">
								Please log in to write a review
							</p>
						) : null}
					</CardContent>
				</Card>
			)}

			{user &&
				userHasReviewed &&
				!showReviewForm &&
				reviews.length > 0 && (
					<div className="text-center mt-8">
						<p className="text-gray-500 mb-2">
							You've already submitted a review for this product
						</p>
					</div>
				)}
		</div>
	);
}
